import * as React from 'react'
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom'

import { FullscreenLoader } from '@components/elements/FullscreenLoader'
import { useAuth } from '@components/providers/Auth'
import { APP_ROUTES } from '@root/appRoutes'

// // // // // // // // //
// Unauthenticated Pages
// // // // // // // // //
const Login = React.lazy(() => import('./Unauthenticated/Login'))
const CreateAccountSelection = React.lazy(() => import('./Unauthenticated/CreateAccountSelection'))
const CustomerSignUpForm = React.lazy(() => import('./Unauthenticated/CustomerSignUpForm'))
const ProviderSignUpForm = React.lazy(() => import('./Unauthenticated/ProviderSignUpForm'))
const RecoverPassword = React.lazy(() => import('./Unauthenticated/RecoverPassword'))
const ResetPassword = React.lazy(() => import('./Unauthenticated/ResetPassword'))

// // // // // // // // //
// Customer Pages
// // // // // // // // //
const CustomerHome = React.lazy(() => import('./Authenticated/Customer/Home'))
const UploadCustomerProfilePic = React.lazy(
	() => import('./Authenticated/Customer/Onboarding/UploadProfilePic')
)
const CreateCustomerAccountSuccess = React.lazy(
	() => import('./Authenticated/Customer/Onboarding/CreateAccountSuccess')
)
const EnableCustomerNotifications = React.lazy(
	() => import('./Authenticated/Customer/Onboarding/EnableNotifications')
)
const NewAddress = React.lazy(() => import('./Authenticated/Customer/NewAddress'))
const UnserviceableLocationAlert = React.lazy(
	() => import('./Authenticated/Customer/NewAddress/UnserviceableLocationAlert')
)
const CustomerAccount = React.lazy(() => import('./Authenticated/Customer/Account'))
const ServiceHistory = React.lazy(() => import('./Authenticated/Customer/ServiceHistory'))
const ViewArchivedRequest = React.lazy(() => import('./Authenticated/Customer/ViewArchivedRequest'))
const Notifications = React.lazy(() => import('./Authenticated/Customer/Notifications'))
const ActiveServiceRequests = React.lazy(
	() => import('./Authenticated/Customer/ActiveServiceRequests')
)
const EditAddress = React.lazy(() => import('./Authenticated/Customer/Account/EditAddress'))
const EditContactDetails = React.lazy(
	() => import('./Authenticated/Customer/Account/EditContactDetails')
)
const NewServiceRequest = React.lazy(() => import('./Authenticated/Customer/NewServiceRequest'))
const RequestProgression = React.lazy(() => import('./Authenticated/Customer/RequestProgression'))
const ViewRequestProvider = React.lazy(() => import('./Authenticated/Customer/ViewRequestProvider'))

// // // // // // // // //
// Provider Pages
// // // // // // // // //
const EnableProviderNotifications = React.lazy(
	() => import('./Authenticated/Provider/Onboarding/EnableNotifications')
)
const ProviderHome = React.lazy(() => import('./Authenticated/Provider/Home'))
const CreateProviderAccountSuccess = React.lazy(
	() => import('./Authenticated/Provider/Onboarding/CreateAccountSuccess')
)
const ActivationRequest = React.lazy(() => import('./Authenticated/Provider/ActivationRequest'))
const ViewRequestDetails = React.lazy(() => import('./Authenticated/Provider/ViewRequestDetails'))
const UnassignedRequests = React.lazy(() => import('./Authenticated/Provider/UnassignedRequests'))
const ActiveRequests = React.lazy(() => import('./Authenticated/Provider/ActiveRequests'))
const JobsMap = React.lazy(() => import('./Authenticated/Provider/JobsMap'))
const RequestCompletion = React.lazy(() => import('./Authenticated/Provider/RequestCompletion'))
const ProviderAccount = React.lazy(() => import('./Authenticated/Provider/Account'))
const EditProviderContactDetails = React.lazy(
	() => import('./Authenticated/Provider/Account/EditContactDetails')
)
const EditTravelDistance = React.lazy(
	() => import('./Authenticated/Provider/Account/EditTravelDistance')
)
const EditEquipment = React.lazy(() => import('./Authenticated/Provider/Account/EditEquipment'))
const EditVehicle = React.lazy(() => import('./Authenticated/Provider/Account/EditVehicle'))
const EditDriversLicense = React.lazy(
	() => import('./Authenticated/Provider/Account/EditDriversLicense')
)
const EditInsurance = React.lazy(() => import('./Authenticated/Provider/Account/EditInsurance'))
const EditAccountPreferences = React.lazy(
	() => import('./Authenticated/Provider/Account/EditAccountPreferences')
)
const ViewRequestCustomer = React.lazy(() => import('./Authenticated/Provider/ViewRequestCustomer'))
const JobHistory = React.lazy(() => import('./Authenticated/Provider/JobHistory'))
const ViewArchivedJob = React.lazy(() => import('./Authenticated/Provider/ViewArchivedJob'))
const IncompleteAccount = React.lazy(
	() => import('./Authenticated/Provider/Stripe/IncompleteAccount')
)
const CheckAccountStatus = React.lazy(
	() => import('./Authenticated/Provider/Stripe/CheckAccountStatus')
)
const AwaitingQuikReview = React.lazy(() => import('./Authenticated/Provider/AwaitingQuikReview'))

// // // // // // // // //
// Shared Auth Pages
// // // // // // // // //
const NotFound = React.lazy(() => import('./Authenticated/Shared/NotFound'))
const Support = React.lazy(() => import('./Authenticated/Shared/Support'))
const Contact = React.lazy(() => import('./Authenticated/Shared/Contact'))
const CustomerFAQ = React.lazy(() => import('./Authenticated/Shared/Support/CustomerFAQ'))
const ProviderFAQ = React.lazy(() => import('./Authenticated/Shared/Support/ProviderFAQ'))
const ContactSubmission = React.lazy(
	() => import('./Authenticated/Shared/ContactSubmissionSuccess')
)

// // // // // // // // //
// Unauthenticated Routes
// // // // // // // // //
const unauthenticatedRoutes = [
	{
		exact: true,
		key: APP_ROUTES.unauthenticated.login,
		path: APP_ROUTES.unauthenticated.login,
		component: Login,
	},
	{
		exact: true,
		key: APP_ROUTES.unauthenticated.createAccountPage,
		path: APP_ROUTES.unauthenticated.createAccountPage,
		component: CreateAccountSelection,
	},
	{
		exact: true,
		key: APP_ROUTES.unauthenticated.customerSignUpPage,
		path: APP_ROUTES.unauthenticated.customerSignUpPage,
		component: CustomerSignUpForm,
	},
	{
		exact: true,
		key: APP_ROUTES.unauthenticated.providerSignUpPage,
		path: APP_ROUTES.unauthenticated.providerSignUpPage,
		component: ProviderSignUpForm,
	},
	{
		exact: true,
		key: APP_ROUTES.unauthenticated.recoverPassword,
		path: APP_ROUTES.unauthenticated.recoverPassword,
		component: RecoverPassword,
	},
	{
		exact: true,
		key: APP_ROUTES.unauthenticated.resetPassword,
		path: APP_ROUTES.unauthenticated.resetPassword,
		component: ResetPassword,
	},
]

// // // // // // // // //
// Authenticated Routes
// // // // // // // // //
const customerRoutes = [
	{
		exact: true,
		key: APP_ROUTES.authenticated.customers.enableNotifications,
		path: APP_ROUTES.authenticated.customers.enableNotifications,
		component: EnableCustomerNotifications,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.customers.createAccountSuccess,
		path: APP_ROUTES.authenticated.customers.createAccountSuccess,
		component: CreateCustomerAccountSuccess,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.customers.uploadProfilePic,
		path: APP_ROUTES.authenticated.customers.uploadProfilePic,
		component: UploadCustomerProfilePic,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.customers.account,
		path: APP_ROUTES.authenticated.customers.account,
		component: CustomerAccount,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.customers.editContactDetails,
		path: APP_ROUTES.authenticated.customers.editContactDetails,
		component: EditContactDetails,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.customers.addOrEditAddressAddressWithParams,
		path: APP_ROUTES.authenticated.customers.addOrEditAddressAddressWithParams,
		component: EditAddress,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.customers.serviceHistory,
		path: APP_ROUTES.authenticated.customers.serviceHistory,
		component: ServiceHistory,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.customers.serviceHistoryWithSlug,
		path: APP_ROUTES.authenticated.customers.serviceHistoryWithSlug,
		component: ViewArchivedRequest,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.customers.activeServiceRequests,
		path: APP_ROUTES.authenticated.customers.activeServiceRequests,
		component: ActiveServiceRequests,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.customers.newAddress,
		path: APP_ROUTES.authenticated.customers.newAddress,
		component: NewAddress,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.customers.unserviceableAlert,
		path: APP_ROUTES.authenticated.customers.unserviceableAlert,
		component: UnserviceableLocationAlert,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.customers.newServiceRequest,
		path: APP_ROUTES.authenticated.customers.newServiceRequest,
		component: NewServiceRequest,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.customers.viewProgressWithSlug,
		path: APP_ROUTES.authenticated.customers.viewProgressWithSlug,
		component: RequestProgression,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.customers.home,
		path: APP_ROUTES.authenticated.customers.home,
		component: CustomerHome,
	},

	{
		exact: true,
		key: APP_ROUTES.authenticated.customers.customerNotifications,
		path: APP_ROUTES.authenticated.customers.customerNotifications,
		component: Notifications,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.customers.viewRequestProviderWithSlug,
		path: APP_ROUTES.authenticated.customers.viewRequestProviderWithSlug,
		component: ViewRequestProvider,
	},
]
const providerRoutes = [
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.enableNotifications,
		path: APP_ROUTES.authenticated.providers.enableNotifications,
		component: EnableProviderNotifications,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.providerNotifications,
		path: APP_ROUTES.authenticated.providers.providerNotifications,
		component: Notifications,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.home,
		path: APP_ROUTES.authenticated.providers.home,
		component: ProviderHome,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.createAccountSuccess,
		path: APP_ROUTES.authenticated.providers.createAccountSuccess,
		component: CreateProviderAccountSuccess,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.activationRequest,
		path: APP_ROUTES.authenticated.providers.activationRequest,
		component: ActivationRequest,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.unassignedRequests,
		path: APP_ROUTES.authenticated.providers.unassignedRequests,
		component: UnassignedRequests,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.jobDetailsWithSlug,
		path: APP_ROUTES.authenticated.providers.jobDetailsWithSlug,
		component: ViewRequestDetails,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.activeRequests,
		path: APP_ROUTES.authenticated.providers.activeRequests,
		component: ActiveRequests,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.jobsMap,
		path: APP_ROUTES.authenticated.providers.jobsMap,
		component: JobsMap,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.requestCompletionWithSlug,
		path: APP_ROUTES.authenticated.providers.requestCompletionWithSlug,
		component: RequestCompletion,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.account,
		path: APP_ROUTES.authenticated.providers.account,
		component: ProviderAccount,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.editContactDetails,
		path: APP_ROUTES.authenticated.providers.editContactDetails,
		component: EditProviderContactDetails,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.editTravelDistance,
		path: APP_ROUTES.authenticated.providers.editTravelDistance,
		component: EditTravelDistance,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.editEquipment,
		path: APP_ROUTES.authenticated.providers.editEquipment,
		component: EditEquipment,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.editVehicle,
		path: APP_ROUTES.authenticated.providers.editVehicle,
		component: EditVehicle,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.editDriversLicense,
		path: APP_ROUTES.authenticated.providers.editDriversLicense,
		component: EditDriversLicense,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.editInsurance,
		path: APP_ROUTES.authenticated.providers.editInsurance,
		component: EditInsurance,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.editAccountPreferences,
		path: APP_ROUTES.authenticated.providers.editAccountPreferences,
		component: EditAccountPreferences,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.viewRequestCustomerWithSlug,
		path: APP_ROUTES.authenticated.providers.viewRequestCustomerWithSlug,
		component: ViewRequestCustomer,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.jobHistory,
		path: APP_ROUTES.authenticated.providers.jobHistory,
		component: JobHistory,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.jobHistoryWithSlug,
		path: APP_ROUTES.authenticated.providers.jobHistoryWithSlug,
		component: ViewArchivedJob,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.stripeIncompleteAccount,
		path: APP_ROUTES.authenticated.providers.stripeIncompleteAccount,
		component: IncompleteAccount,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.stripeCheckAccountStatus,
		path: APP_ROUTES.authenticated.providers.stripeCheckAccountStatus,
		component: CheckAccountStatus,
	},
	{
		exact: true,
		key: APP_ROUTES.authenticated.providers.awaitingQuikplowReview,
		path: APP_ROUTES.authenticated.providers.awaitingQuikplowReview,
		component: AwaitingQuikReview,
	},
]
const sharedAuthRoutes = [
	{
		exact: true,
		key: APP_ROUTES.shared.notFound,
		path: APP_ROUTES.shared.notFound,
		component: NotFound,
	},
	{
		exact: true,
		key: APP_ROUTES.shared.support,
		path: APP_ROUTES.shared.support,
		component: Support,
	},
	{
		exact: true,
		key: APP_ROUTES.shared.customerFaq,
		path: APP_ROUTES.shared.customerFaq,
		component: CustomerFAQ,
	},
	{
		exact: true,
		key: APP_ROUTES.shared.providerFaq,
		path: APP_ROUTES.shared.providerFaq,
		component: ProviderFAQ,
	},
	{
		exact: true,
		key: APP_ROUTES.shared.contactUs,
		path: APP_ROUTES.shared.contactUs,
		component: Contact,
	},
	{
		exact: true,
		key: APP_ROUTES.shared.contactSubmission,
		path: APP_ROUTES.shared.contactSubmission,
		component: ContactSubmission,
	},
]

export const Routes: React.FC = () => {
	const { user, accountType } = useAuth()
	const location = useLocation()
	const history = useHistory()

	const attemptedUserFetch = user !== undefined
	const isAuthenticated = user && (accountType === 'customers' || accountType === 'providers')

	React.useEffect(() => {
		if (location?.pathname.startsWith('/uni/')) {
			history.push(`${location.pathname.replace('/uni/', '/')}${location.search}${location.hash}`)
		}
	}, [location, history])

	return (
		<React.Suspense fallback={<FullscreenLoader />}>
			{attemptedUserFetch && !isAuthenticated && (
				<Switch>
					{unauthenticatedRoutes.map((route) => (
						<Route {...route} />
					))}

					<Route>
						<Redirect to={APP_ROUTES.unauthenticated.login} />
					</Route>
				</Switch>
			)}

			{attemptedUserFetch && isAuthenticated && (
				<Switch>
					{accountType === 'customers' && customerRoutes.map((route) => <Route {...route} />)}

					{accountType === 'providers' && providerRoutes.map((route) => <Route {...route} />)}

					{sharedAuthRoutes.map((route) => (
						<Route {...route} />
					))}

					<Route>
						<Redirect exact from="/" to="/home" />
					</Route>

					<Route>
						<Redirect to={APP_ROUTES.shared.notFound} />
					</Route>
				</Switch>
			)}
		</React.Suspense>
	)
}
